import React from 'react'
import { Platform } from 'react-native'
import {
  createWebModalHistoryLayer,
  type WebModalHistoryLayer,
} from '../lib/webModalHistory'

interface UseWebModalHistoryOptions {
  visible: boolean
  canDismiss: boolean
  onDismiss: () => void
}

interface DismissOptions {
  /** Reuse this overlay's history entry for another overlay on the same route. */
  reuseHistoryEntry?: boolean
}

/** Make the first browser-back action dismiss a visible modal on web. */
export function useWebModalHistory({
  visible,
  canDismiss,
  onDismiss,
}: UseWebModalHistoryOptions): (afterDismiss: () => void, options?: DismissOptions) => void {
  const canDismissRef = React.useRef(canDismiss)
  const onDismissRef = React.useRef(onDismiss)
  const layerRef = React.useRef<WebModalHistoryLayer | null>(null)
  const unmountTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null)

  canDismissRef.current = canDismiss
  onDismissRef.current = onDismiss

  React.useEffect(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined') return

    // React Strict Mode mounts effects twice in development. Deferring final
    // cleanup lets the second setup retain the one history entry and listener.
    if (unmountTimerRef.current !== null) {
      clearTimeout(unmountTimerRef.current)
      unmountTimerRef.current = null
    }

    if (layerRef.current === null) {
      layerRef.current = createWebModalHistoryLayer({
        history: window.history,
        events: window,
        canDismiss: () => canDismissRef.current,
        onDismiss: () => onDismissRef.current(),
      })
    }

    return () => {
      unmountTimerRef.current = setTimeout(() => {
        layerRef.current?.release()
        layerRef.current?.dispose()
        layerRef.current = null
        unmountTimerRef.current = null
      }, 0)
    }
  }, [])

  React.useEffect(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined') return
    if (visible) layerRef.current?.acquire()
    else layerRef.current?.release()
  }, [visible])

  return React.useCallback((afterDismiss: () => void, options?: DismissOptions) => {
    const layer = layerRef.current
    if (Platform.OS === 'web' && layer) {
      const finishDismiss = () => {
        onDismissRef.current()
        afterDismiss()
      }
      if (options?.reuseHistoryEntry) layer.handoff(finishDismiss)
      else layer.releaseAndThen(finishDismiss)
      return
    }

    onDismissRef.current()
    afterDismiss()
  }, [])
}
